import { ReactiveFormHostDuplicatePolicy } from './reactive-form-host-duplicate-policy';
import { ReactiveFormHostModeResolver } from './reactive-form-host-mode-resolver';
import {
    ReactiveFormHostRequestButtonState,
    ReactiveFormHostRequestButtonType,
} from './reactive-form-host-request-button-state';
import { type ReactiveFormHostMode, ReactiveFormHostState } from './reactive-form-host-state';
import { ReactiveFormHostTitleState } from './reactive-form-host-title-state';
import { ReactiveFormHostUndoState } from './reactive-form-host-undo-state';

export type ReactiveFormHostRequestSource = 'put' | 'post' | 'none';

export interface ReactiveFormHostFormSetupTitles {
    readonly titleWhenEdit: string;
    readonly titleWhenNew: string;
    readonly titleWhenDuplicate: string;
    readonly titleWhenRead?: string | undefined;
}

export interface ReactiveFormHostFormSetupOptions<TModel> {
    readonly state: ReactiveFormHostState<TModel>;
    readonly flag: string | null | undefined;
    readonly model: TModel | null | undefined;
    readonly titles: ReactiveFormHostFormSetupTitles;
    readonly fieldsToDelete?: readonly string[] | undefined;
    readonly duplicateIdField?: string | undefined;
    readonly duplicateMutate?: boolean | undefined;
    readonly undoEnabledModes?: readonly ReactiveFormHostMode[] | undefined;
    readonly undoEnabled?: boolean | undefined;
    readonly requestButtonFallbackType?: ReactiveFormHostRequestButtonType | undefined;
}

export interface ReactiveFormHostFormSetupResult<TModel> {
    readonly mode: ReactiveFormHostMode;
    readonly model: TModel;
    readonly title: string;
    readonly undoEnabled: boolean;
    readonly requestButtonType: ReactiveFormHostRequestButtonType;
    readonly requestSource: ReactiveFormHostRequestSource;
}

/** Coordinates the generic host setup values while the app keeps concrete services and requests. */
export class ReactiveFormHostFormSetupCoordinator {
    public static resolve<TModel>(options: ReactiveFormHostFormSetupOptions<TModel>): ReactiveFormHostFormSetupResult<TModel> {
        const mode = ReactiveFormHostModeResolver.normalizeMode(options.flag);

        options.state.setMode(mode);

        const model = mode === 'duplicate'
            ? ReactiveFormHostDuplicatePolicy.apply<TModel>({
                model: options.model,
                fieldsToDelete: options.fieldsToDelete,
                idField: options.duplicateIdField,
                mutate: options.duplicateMutate,
            })
            : options.model ?? ({} as TModel);

        options.state.setModel(model);

        const title = ReactiveFormHostTitleState.resolve({
            mode,
            titleWhenEdit: options.titles.titleWhenEdit,
            titleWhenNew: options.titles.titleWhenNew,
            titleWhenDuplicate: options.titles.titleWhenDuplicate,
            titleWhenRead: options.titles.titleWhenRead,
        });
        const undoEnabled = ReactiveFormHostUndoState.resolve({
            state: options.state,
            enabledModes: options.undoEnabledModes,
            explicitValue: options.undoEnabled,
        });
        const requestButtonType = ReactiveFormHostRequestButtonState.resolve({
            state: options.state,
            fallbackType: options.requestButtonFallbackType,
        });

        return {
            mode,
            model,
            title,
            undoEnabled,
            requestButtonType,
            requestSource: ReactiveFormHostFormSetupCoordinator.resolveRequestSource(mode),
        };
    }

    public static resolveRequestSource(mode: ReactiveFormHostMode): ReactiveFormHostRequestSource {
        switch (mode) {
        case 'edit':
            return 'put';
        case 'new':
        case 'duplicate':
            return 'post';
        default:
            return 'none';
        }
    }
}
